define((require) => {
    "use strict";

    const Class         = require("shared/util/Class");
    const Window        = require("shared/services/Window");
    const $             = require("jquery");
    const _             = require("underscore");
    const {Logger}        = require("shared/util/Logger");
    const Configuration = require("shared/services/Configuration");
    const Promise       = require("shared/util/Promise");
    const GUID          = require("shared/util/GUID");

    const Context = {
        VIEWER:   "viewer",
        CHANNELS: "channels",
        EDITOR:   "editor"
    };

    let sequence = 0;

    /**
     * Abstract base class for logging/sending errors to the SQS error queue. <p>
     *
     * It will also set a window.onerror handler to catch all unhandled errors, optionally reporting them to the
     * frontend error service and/or alert the user with a message detailing the nature of the problem via
     * the configured <tt>ErrorHandler</tt> if and only if the log level is <tt>LOG</tt> or above. <p>
     *
     * Errors will only be logged via the frontend error service if the error logger is "enabled", but will still
     * be reported via <tt>ErrorHandler</tt> (providing the log level is set). <p>
     *
     * An error logger can also be associated with a statsD client, which on logged *handled* errors will
     * call <tt>statsD.traceError()</tt> to track such errors in statsD; the statsD client uses its own global
     * error handler for unhandled errors to track errors, which is why we do not call statsD on unhandled
     * errors here.
     *
     * @class ErrorLogger
     *
     * @author Kim Knudsen
     * @author Gunni Rode
     */
    const ErrorLogger = Class.extend({

        _logger:        null,
        _errorQueueURL: null,
        _context:       null,
        _statsD:        null,

        // Array of previously obtained error ids to be submitted with the next error logged to get a better
        // context of which frontend errors potentially triggers other errors...
        _errors:  [],

        enabled:  false, // enabled or not? Note: will still alert the user on unhandled errors via ErrorHandler for log level >= LOG

        /**
         * Constructor.
         *
         * @param  {String}          errorQueueURL  The SQS Queue url used to post messages.
         * @param  {Context}         context  The logger 'context', as a ErrorLogger.Context.XXX constant value.
         *
         * @constructor
         */
        constructor(errorQueueURL, context) {
            this._errorQueueURL = errorQueueURL;
            this._context = context;

            this._logger = new Logger("ErrorLogger");

            // Add global hook to unhandled errors...
            Window.error((resolved) => {
                const message = {
                    unhandled: resolved.message,
                    trace:     resolved.error.stack && resolved.error.stack.toString().split(/\s*\n+\s*/g),
                    errorType: resolved.error.name || "Unknown"
                };

                // Last false argument is to *not* report stats via statsD on such unhandled errors, since the
                // StatsD implementation already have such a global error handler by itself...
                this.log(message, false); // may not be "enabled"!
            });
        },

        /**
         * Formats the supplied message object (error and data) into a json string.
         *
         * @param  {Object} errorObject  Object to format into a json string; never null.
         * @return {String} The json'ified string; never null.
         */
        _toJSON(errorObject) {
            try {
                // Could be trouble if the actual message cannot be json'ified, e.g. because of circular errors, so on
                // error we try again without it to see if that helps; if not, we sent a message saying so...
                return JSON.stringify(errorObject);
            } catch (e) {
                this._logger.error("Could not format message for frontend error service, removing actual message", e);
                delete errorObject.errorMessage;
                try {
                    return JSON.stringify(errorObject);
                } catch (ee) {
                    this._logger.error("Could not format message for frontend error service, using default message", e);
                    return `{message:"Cannot report error, could not format actual message into json", error:"${ee.message}", url:"${window.location.url}"}`;
                }
            }
        },

        /**
         * Performs a POST request containing the error message and any additional info, as a
         * JSON formatted string.
         *
         * @param  {Object} errorObject Object hash containing the error information.
         * @return {Promise} A promise that resolves once the error is logged; never falsey.
         */
        _post(errorObject) {
            // Send along any previous errors...
            errorObject.errors = this._errors;
            errorObject.sequence = ++sequence;

            this._logger.info(`Error sequence: <${errorObject.sequence}>`);

            // Compose the POST request to the SQS queue.
            return $.ajax({
                url: this._errorQueueURL,
                type: "POST",
                data: `Action=SendMessage&MessageBody=${encodeURIComponent(this._toJSON(errorObject))}`,
                contentType: "application/x-www-form-urlencoded; charset=UTF-8" // Needs to be url encoded and to have the charset set
            }).done(() => {
                // Save the uuid for the next error submitted for better context...
                this._errors.push(errorObject.id);
                this._logger.info(`Error sequence -> uuid: <${errorObject.id}> -> <${errorObject.sequence}>`);
            }).fail((xhr) => {
                this._logger.error(`Could not report frontend error: <${errorObject.sequence}> with id ${errorObject.id}`, xhr);
            });
        },

        /**
         * Subclass hook to provide additional logging information.
         *
         * @return {Object} Object hash that will be appended to the log object
         * sent to the server.
         */
        _additionalInfo() {
            return {};
        },

        /**
         * Associate, or not, the supplied statsD client with this error handler, to be notified each time
         * an error is logged.
         *
         * @param statsD The statsD client, if any; can be null/undefined.
         */
        statsD(statsD) {
            this._statsD = statsD;
        },

        /**
         * Logs the specified error message and sends it to the backend error log.
         *
         * @param  {String|*} message The error message.
         * @param  {Boolean} [statsD] True or undefined to log statsD if a statsD client is set, false otherwise. The
         *                            <tt>traceError</tt> method will be used to report the statsD error.
         * @return {Promise} A promise that resolves once the error has been reported; never falsey. If this error
         *         logger is not enabled, the returned promise is already resolved.
         */
        log(message, statsD) {
            if (!this.enabled) {
                return Promise.resolved();
            }

            const errorType = message.errorType ? message.errorType : "Unknown";
            const isUnhandled = message.unhandled;
            const description = "Frontend error";
            const errorObject = {
                description,
                id:        GUID.random(),
                userAgent: Window.userAgent,
                context:   this._context,
                // This needs to be a String, it cannot be a JSON object because the message is sometimes a JSON
                // object and sometimes a String and ElasticSearch will create an index based on previously reported
                // information and it will reject other messages that don't have the same format
                detail:    `ErrorDescription : ${JSON.stringify(message)}`,
                msg:       `description - ${errorType} - ${isUnhandled ? "unhandled" : "checked"}`,
                url:       Window.topWindow().location.href // embedded urls handled explicitly in ViewerErrorLogger
            };

            // Special trick to mark unhandled errors, needed to be reported even if the json formatting of
            // "message" fails!...
            if (isUnhandled) {
                errorObject.unhandled = isUnhandled.toString(); // make sure it can be marshalled!
                delete message.unhandled;
            }

            // Make sure some random data error will no corrupt the sending of the error...
            try {
                _.extend(errorObject, this._additionalInfo());
            } catch (e) {
                errorObject.additionalInfoFailed = e.toString();
            }

            // Add version, if defined...
            if (Configuration.version) {
                errorObject.version = Configuration.version;
            }

            const promise = this._post(errorObject);

            // Track statsD? May not want to on unhandled errors, since statsD already has such a handler...
            if ((typeof statsD === "undefined" || statsD) && this._statsD) {
                this._statsD.trackError();
            }

            // Track the front end error
            if (this._statsD) {
                this._statsD.trackFrontEndError(errorType);
            }

            return promise;
        }

    }, {
        Context
    });

    return ErrorLogger;
});



// WEBPACK FOOTER //
// ./src/shared/services/error/ErrorLogger.js